import React, { useEffect } from 'react';
import { Flex } from 'antd';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { css } from 'styled-components';

import { getDevExEnv } from 'jf/utils/getDevExEnv';

import { DevExIncidentBanner } from './DevExIncidentBanner';
import { DevExNavBar, NAV_BAR_HEIGHT } from './DevExNavBar';

const styles = {
  outlet: css`
    overflow: hidden;

    &:has(#survey-reveal) {
      overflow: unset;
    }
  `,
};

type DevExOutletProps = {
  reqAuth?: boolean;
  showNav?: boolean;
};

const IS_IN_IFRAME = window.self !== window.top;
const EMP_ORIGIN = getDevExEnv().appOrigin;

export const DevExOutlet: React.FC<DevExOutletProps> = (props) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // scroll to top when changing page
  useEffect(() => window.scrollTo(0, 0), [pathname]);

  const shouldShowNav = props.showNav && !IS_IN_IFRAME;

  // Listen for navigation requests from the parent Jellyfish frame
  useEffect(() => {
    if (!IS_IN_IFRAME) {
      return;
    }
    const handleEmpNavigation = (event: MessageEvent) => {
      if (
        event.origin === EMP_ORIGIN &&
        event.data?.type === 'jf-navigate' &&
        typeof event.data.path === 'string'
      ) {
        navigate(event.data.path);
      }
    };
    window.addEventListener('message', handleEmpNavigation);
    return () => window.removeEventListener('message', handleEmpNavigation);
  }, [navigate]);

  // Sync route changes to the parent frame so the EMP URL stays in sync
  useEffect(() => {
    if (!IS_IN_IFRAME) {
      return;
    }
    window.parent.postMessage({ type: 'devex-navigate', path: pathname }, EMP_ORIGIN);
  }, [pathname]);

  return (
    <React.Suspense fallback={[]}>
      <div css={styles.outlet}>
        {shouldShowNav && <DevExNavBar key="navbar" />}
        <Flex
          vertical
          style={{
            minHeight: '100vh',
            paddingTop: shouldShowNav ? NAV_BAR_HEIGHT : 0,
          }}
        >
          {props.reqAuth && <DevExIncidentBanner />}
          <Outlet />
        </Flex>
      </div>
    </React.Suspense>
  );
};
